/* eslint-disable react/no-danger */
import React from "react";
// ANT DESIGN COMPONENTS
import { Collapse, Select } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { faFileCode } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import {
  AdapterCdoOpcInterface,
  ExpandedMappingsInterface,
  LocationInterface,
  OpcMappingsInterface,
  CarNum2NameEntry,
} from "../../../../../interfaces/AdapterCdoOpc.interface";
import { RootState } from "../../../../../app/store";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import DrawerRowDivider from "../../../../components/drawer/DrawerRowDivider";
import { VendorAddressing } from "../../../../../flatbuffers/generated/opc/vendor-addressing";
import CdoOpcLocation from "./CdoOpcLocation";
import CdoOpcMappingPage from "./CdoOpcMappingPage";
import CdoOpcCarNumberToName from "./CdoOpcCarNumberToName";

const BLANK_OPC_MAPPINGS: OpcMappingsInterface = {
  opc_cmd_to_msg: [],
  msg_to_opc_cmd: [],
  opc_cmd_to_sequence: [],
};

/**
 * Fills in any expansion bucket the stored configuration left out.
 *
 * `extractCdoState` copies `groups` and `lifts` straight out of the adapter's
 * stored `cdo_input` behind a cast, with no merge against BLANK_ADAPTER_CDO_OPC,
 * so whatever the server persisted is what the panels below are handed. The Go
 * Object API writes these buckets as pointers with `omitempty`, so a site that
 * simply has no path-only expansions serializes without the key at all -- and
 * the panel that reads it would then dereference `undefined.mappings` and take
 * the whole editor down with it. Materialising the empty buckets is also the
 * shape the schema asks for: all four fields of Opc.ExpandedOpcMappings are
 * marked required.
 */
function withEveryBucket(source: ExpandedMappingsInterface): ExpandedMappingsInterface {
  return {
    mappings: source?.mappings ?? BLANK_OPC_MAPPINGS,
    path_only: { mappings: source?.path_only?.mappings ?? BLANK_OPC_MAPPINGS },
    data_only: { mappings: source?.data_only?.mappings ?? BLANK_OPC_MAPPINGS },
    path_and_data: { mappings: source?.path_and_data?.mappings ?? BLANK_OPC_MAPPINGS },
  };
}

/**
 * Fills in the location the stored configuration left out.
 *
 * `location` reaches this page by the same route as the mapping buckets -- straight out
 * of the stored `cdo_input` behind a cast -- and the Go Object API writes it as a pointer
 * with `omitempty` too, so an adapter whose location was never filled in serializes
 * without the key. Three reads dereference it unconditionally: `copy.location.name` in
 * `onChangeFormValue` below, and `location.name` plus `location.sites.map` in
 * CdoOpcLocation. Any one of them takes the whole editor down on mount.
 *
 * The nesting is filled in all the way down, not just at the top. `Location.Sites`,
 * `Site.Groups` and `Group.Lifts` each carry `omitempty` in the generated Go structs
 * (flatbuffer_schemas/go_src/Opc/{Location,Site,Group}.go), and an empty slice is dropped
 * independently of its parent -- so a site with no groups, or a group with no lifts,
 * comes back without the key while everything above it is present. CdoOpcLocation maps
 * over both unconditionally, so each level is its own crash.
 *
 * Returned fresh on every call rather than from a shared constant: the value is handed
 * to a child that may hold it across renders, and a shared default would be aliased into
 * every OPC adapter at once.
 */
function withLocation(source: LocationInterface): LocationInterface {
  return {
    name: source?.name ?? "",
    sites: (source?.sites ?? []).map((site) => ({
      ...site,
      groups: (site?.groups ?? []).map((group) => ({
        ...group,
        lifts: group?.lifts ?? [],
      })),
    })),
  };
}

export default function CdoOpcPage({
  setCdo,
  cdoValue,
}: {
  setCdo: (newValue: AdapterCdoOpcInterface) => void;
  cdoValue: AdapterCdoOpcInterface;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { theme } = useSelector((state: RootState) => state.home);
  const { Panel } = Collapse;
  const [t] = useTranslation("global");

  // Every read and every setter below works from this, never from the raw prop, so a
  // field missing from the stored payload cannot crash either direction. `location` is
  // covered here for the same reason the mapping buckets are -- see withLocation.
  const cdo: AdapterCdoOpcInterface = {
    ...cdoValue,
    location: withLocation(cdoValue.location),
    sites: cdoValue.sites ?? BLANK_OPC_MAPPINGS,
    locations: cdoValue.locations ?? BLANK_OPC_MAPPINGS,
    groups: withEveryBucket(cdoValue.groups),
    lifts: withEveryBucket(cdoValue.lifts),
  };

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const isLeafAddressing =
    (cdo.vendor_addressing ?? VendorAddressing.Absolute) === VendorAddressing.Leaf;

  const setAddressingMode = (leaf: boolean) => {
    const copy: AdapterCdoOpcInterface = structuredClone(cdo);
    copy.vendor_addressing = leaf ? VendorAddressing.Leaf : VendorAddressing.Absolute;
    setCdo(copy);
  };

  const onChangeFormValue = (newValue: string | number, variableName: string) => {
    const copy: AdapterCdoOpcInterface = structuredClone(cdo);
    if (variableName === "opc_server" && typeof newValue === "string") {
      copy.opc_server = newValue;
    } else if (variableName === "hostname" && typeof newValue === "string") {
      copy.hostname = newValue;
    } else if (variableName === "locationName" && typeof newValue === "string") {
      copy.location.name = newValue;
    } else if (variableName === "vendor_path_literal" && typeof newValue === "string") {
      copy.vendor_path_literal = newValue;
    }
    setCdo(copy);
  };

  const formatJsonWithColors = (obj: any) => {
    const json = JSON.stringify(obj, null, 4)
      .replace(/"([^"]+)"\s*:/g, '<span style="color:#9cdcfe;">"$1"</span>:')
      .replace(/: "([^"]*)"/g, ': <span style="color:#ce9178;">"$1"</span>')
      .replace(/:\s*(\d+)/g, ': <span style="color:#b5cea8;">$1</span>');
    return json;
  };

  const setCdoOpcLocation = (newValue: LocationInterface) => {
    const copy = structuredClone(cdo);
    copy.location = newValue;
    setCdo(copy);
  };

  const setCdoOpcSites = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.sites = newValue;
    setCdo(copy);
  };

  const setCdoOpcLocations = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.locations = newValue;
    setCdo(copy);
  };

  const setCdoOpcCarNumberToName = (newValue: CarNum2NameEntry[]) => {
    const copy = structuredClone(cdo);
    copy.car_num_to_name = newValue;
    setCdo(copy);
  };

  const setCdoOpcGroups = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.groups.mappings = newValue;
    setCdo(copy);
  };

  const setCdoOpcGroupsPathOnly = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.groups.path_only.mappings = newValue;
    setCdo(copy);
  };

  const setCdoOpcGroupsDataOnly = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.groups.data_only.mappings = newValue;
    setCdo(copy);
  };

  const setCdoOpcGroupsPathAndData = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.groups.path_and_data.mappings = newValue;
    setCdo(copy);
  };

  const setCdoOpcLifts = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.lifts.mappings = newValue;
    setCdo(copy);
  };

  const setCdoOpcLiftsPathOnly = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.lifts.path_only.mappings = newValue;
    setCdo(copy);
  };

  const setCdoOpcLiftsDataOnly = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.lifts.data_only.mappings = newValue;
    setCdo(copy);
  };

  const setCdoOpcLiftsPathAndData = (newValue: OpcMappingsInterface) => {
    const copy = structuredClone(cdo);
    copy.lifts.path_and_data.mappings = newValue;
    setCdo(copy);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <DrawerRowDivider icon={faFileCode} title="OPC" />
      <Collapse>
        <Panel header={t("general.opcJsonPreview")} key="1">
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <pre
                style={{
                  width: "100%",
                  color: "#dcdcdc",
                  padding: "10px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  overflowX: "auto",
                }}
                dangerouslySetInnerHTML={{
                  __html: formatJsonWithColors(cdo),
                }}
              />
            </div>
          </div>
        </Panel>
      </Collapse>
      <br />
      <Collapse>
        <Panel header={t("general.opcFormulary")} key="1">
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <FormInputText
                inputValue={cdo.opc_server}
                inputLabelName={t("general.opcServer")}
                inputOnChange={onChangeFormValue}
                inputVariableName="opc_server"
                isRequired
                disabledInput={false}
              />
              <FormInputText
                inputValue={cdo.hostname}
                inputLabelName={t("general.hostName")}
                inputOnChange={onChangeFormValue}
                inputVariableName="hostname"
                isRequired
                disabledInput={false}
              />
            </div>
          </div>
        </Panel>
      </Collapse>
      {/**
       * ADDRESSING MODE
       *
       * Leaf mode is what lets one adapter serve every site of a controller
       * type: the mappings below hold only bottom-level leaf item names, and
       * LiftCloud synthesizes the Location/Site/Group/Lift tree per bank from
       * the segments entered on the Bank and Lifts pages.
       *
       * Absolute is the default, so every adapter authored before this feature
       * keeps its stored tree and its exact behaviour.
       */}
      <DrawerRowDivider icon={faFileCode} title={t("general.opcAddressing")} />
      <div className="row">
        <div className="col-12 col-md-4 mt-2">
          <span className="generalStyles__inputLabel">Addressing Mode</span>
          <Select
            className="generalStyles__width100"
            size="small"
            value={isLeafAddressing ? "leaf" : "absolute"}
            onChange={(value) => setAddressingMode(value === "leaf")}
            options={[
              { value: "absolute", label: "Absolute — site data stored on this adapter" },
              { value: "leaf", label: "Leaf — one adapter, site data entered per bank" },
            ]}
          />
        </div>
        {isLeafAddressing && (
          <div className="col-12 col-md-4 mt-2">
            <FormInputText
              inputValue={cdoValue.vendor_path_literal ?? ""}
              inputLabelName="Controller Path Literal"
              inputOnChange={onChangeFormValue}
              inputVariableName="vendor_path_literal"
              isRequired={false}
              disabledInput={false}
            />
          </div>
        )}
      </div>
      {/**
       * LOCATION + CAR NUMBER TO NAME — legacy, Absolute adapters only.
       *
       * Under Leaf addressing these are synthesized per bank, so editing them
       * here would be writing values nothing reads. The editors stay mounted
       * for Absolute adapters so an unmigrated site remains fully editable.
       */}
      {!isLeafAddressing && (
        <CdoOpcLocation setCdoOpcLocation={setCdoOpcLocation} location={cdo.location} />
      )}
      {/** SITES */}
      <CdoOpcMappingPage
        setValue={setCdoOpcSites}
        title={t("general.opcSitesMapping")}
        opcMapping={cdo.sites}
      />
      {/** LOCATIONS */}
      <CdoOpcMappingPage
        setValue={setCdoOpcLocations}
        title={t("general.opcLocationsMapping")}
        opcMapping={cdo.locations}
      />
      {/** GROUPS */}
      <CdoOpcMappingPage
        setValue={setCdoOpcGroups}
        title={t("general.opcGroupsMapping")}
        opcMapping={cdo.groups.mappings}
      />
      {/** GROUPS EXPANDED PATH ONLY */}
      <CdoOpcMappingPage
        setValue={setCdoOpcGroupsPathOnly}
        title={t("general.opcGroupsExpandedPathOnly")}
        opcMapping={cdo.groups.path_only.mappings}
      />
      {/** GROUPS EXPANDED DATA ONLY */}
      <CdoOpcMappingPage
        setValue={setCdoOpcGroupsDataOnly}
        title={t("general.opcGroupsExpandedDataOnly")}
        opcMapping={cdo.groups.data_only.mappings}
      />
      {/** GROUPS EXPANDED PATH AND DATA */}
      <CdoOpcMappingPage
        setValue={setCdoOpcGroupsPathAndData}
        title={t("general.opcGroupsExpandedPathAndData")}
        opcMapping={cdo.groups.path_and_data.mappings}
      />
      {/** LIFTS */}
      <CdoOpcMappingPage
        setValue={setCdoOpcLifts}
        title={t("general.opcLiftsMapping")}
        opcMapping={cdo.lifts.mappings}
      />
      {/** LIFTS EXPANDED PATH ONLY */}
      <CdoOpcMappingPage
        setValue={setCdoOpcLiftsPathOnly}
        title={t("general.opcLiftsExpandedPathOnly")}
        opcMapping={cdo.lifts.path_only.mappings}
      />
      {/** LIFTS EXPANDED DATA ONLY */}
      <CdoOpcMappingPage
        setValue={setCdoOpcLiftsDataOnly}
        title={t("general.opcLiftsExpandedDataOnly")}
        opcMapping={cdo.lifts.data_only.mappings}
      />
      {/** LIFTS EXPANDED PATH AND DATA */}
      <CdoOpcMappingPage
        setValue={setCdoOpcLiftsPathAndData}
        title={t("general.opcLiftsExpandedPathAndData")}
        opcMapping={cdo.lifts.path_and_data.mappings}
      />
      {/** CAR TO NUMBER NAME — legacy, Absolute adapters only (see above) */}
      {!isLeafAddressing && (
        <CdoOpcCarNumberToName
          setCdoOpcCarNumberToName={setCdoOpcCarNumberToName}
          carNumbersToNames={cdo.car_num_to_name}
        />
      )}
    </div>
  );
}
